import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import Breadcrumb from '../../../../../components/Common/Breadcrumb'
import Request from '../../../../../helpers/request'
import * as url from '../../../../../helpers/url_helper'
import { convertLocalDate } from '../../../../../Utils/configController'
import Overview from './tabs/Overview/Overview'
import PlaceModules from './tabs/Modules/PlaceModules'
import PlaceAppliances from './tabs/Appliances/PlaceAppliances'
import PlaceManualControls from './tabs/ManualControls/PlaceManualControls'
import PlaceUsers from './tabs/PlaceUsers/PlaceUsers'
import PlaceLogs from './tabs/PlaceLogs/PlaceLogs'
import PlaceHealth from './tabs/PlaceHealth/PlaceHealth'
import NetworkManagement from './tabs/NetworkManagement/NetworkManagement'

const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'modules', label: 'Modules' },
    { key: 'appliances', label: 'Appliances' },
    { key: 'manual-controls', label: 'Manual Controls' },
    { key: 'users', label: 'Users' },
    { key: 'logs', label: 'Logs' },
    { key: 'health', label: 'Health' },
    { key: 'network-management', label: 'Network Management' },
]

const Chip = ({ label, value, title }: { label: string; value: string; title?: string }) => (
    <span
        title={title || value}
        style={{
            display: 'inline-flex', alignItems: 'baseline', gap: 4,
            fontSize: 12, color: '#475467',
            padding: '2px 8px', background: '#F3F4F6', borderRadius: 10,
        }}
    >
        <span style={{ color: '#858D9D' }}>{label}</span>
        <span style={{ fontWeight: 500 }}>{value}</span>
    </span>
)

const IdRow = ({ label, value }: { label: string; value: string }) => {
    const onCopy = () => {
        try {
            navigator.clipboard?.writeText(value)
            toast.success(`${label} copied`, { autoClose: 1200 })
        } catch { /* clipboard blocked — silent */ }
    }
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{ color: '#858D9D' }}>{label}</span>
            <code style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#475467' }}>
                {value}
            </code>
            <button
                onClick={onCopy}
                title={`Copy ${label}`}
                style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontSize: 12, color: '#9CA3AF', lineHeight: 1,
                }}
            >
                📋
            </button>
        </span>
    )
}

const PlacePage = () => {
    const { userId, placeId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()

    const [loading, setLoading] = useState(false)
    const [refreshing, setRefreshing] = useState(false)
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
    const [userData, setUserData] = useState<any>(null)
    const [placeList, setPlaceList] = useState<any[]>([])
    const [selectedPlace, setSelectedPlace] = useState<any>(null)
    const [rooms, setRooms] = useState<any[]>([])
    const [pcbs, setPcbs] = useState<any[]>([])
    const [relays, setRelays] = useState<any[]>([])
    const [switches, setSwitches] = useState<any[]>([])
    const [devices, setDevices] = useState<any[]>([])
    const [owners, setOwners] = useState<any[]>([])
    const [members, setMembers] = useState<any[]>([])

    const activeTab = TABS.find(t => location.pathname.endsWith(t.key))?.key ?? 'overview'

    const basePath = `/users/profile/${userId}/places/${placeId}`

    // silent=true → background refresh (manual button / auto-poll). It updates
    // the data states without flipping `loading`, so the active tab, pagination
    // page, open modals and scroll position are all preserved. Only the initial
    // load uses loading=true.
    const fetchData = (silent = false) => {
        if (silent) setRefreshing(true)
        else setLoading(true)
        const reqObj = { userId, roomId: '' }
        Request.post(url.GET_USERS_DETAIL_URL, reqObj)
            .then((res: any) => {
                const data = res.data
                setUserData(data?.data)
                setPlaceList(data?.places || [])
                setRooms(data?.rooms || [])
                setPcbs(data?.pcbs || [])
                setRelays(data?.relays || [])
                setSwitches(data?.switches || [])
                setDevices(data?.devices || [])
                setOwners(data?.owners || [])
                setMembers(data?.members || [])
                setLastUpdated(new Date())
            })
            .catch(() => { if (!silent) toast.error('Failed to load profile') })
            .finally(() => { silent ? setRefreshing(false) : setLoading(false) })
    }

    useEffect(() => {
        fetchData(false)
    }, [userId])

    // selectedPlace is DERIVED from the URL place + fetched list — never set
    // inside fetchData. Field case 2026-08-05: switching places only
    // navigated, so the chip kept the PREVIOUS place's id (a "Test place" id
    // rendered over Dayro A407's live data) until the 30s background refresh.
    // If the URL place isn't in the list (the backend returns OWNED places
    // only — member-access places are absent), synthesize a minimal truthful
    // entry from the URL id instead of silently showing another place.
    useEffect(() => {
        setSelectedPlace(
            placeList.find((p: any) => p.id === placeId)
            || (placeId ? { id: placeId, name: '(place not in owned-places list)' } : placeList[0] || null)
        )
    }, [placeId, placeList])

    // ── Auto-update opt-in (phase 1, 2026-08-03) ────────────────────────
    // Reads the backend OTA policy and shows whether THIS place is opted in
    // to automatic firmware updates (full trigger set). null = still loading.
    // Under global mode 'on' the toggle is moot (everything auto-updates) —
    // shown as informational text instead.
    //
    // Phase 2 adds the EXEMPTION chip: deniedPlaces membership. Deny is
    // absolute — an exempted place receives NO automatic updates in ANY mode
    // (it even beats 'on'), so the exempt chip renders in every mode and,
    // while exempt, the opt-in toggle is disabled (the backend would ignore
    // it anyway). Manual/admin OTA still works for an exempted place.
    const [otaOptIn, setOtaOptIn] = useState<boolean | null>(null)
    const [otaExempt, setOtaExempt] = useState<boolean | null>(null)
    const [otaMode, setOtaMode] = useState<string>('')
    const [otaBusy, setOtaBusy] = useState(false)

    const fetchOtaOptIn = () => {
        Request.post(url.OTA_STATUS, {})
            .then((res: any) => {
                const policy = res?.data?.data?.policy
                setOtaMode(policy?.mode || '')
                const allowed: any[] = policy?.allowedPlaces || []
                setOtaOptIn(allowed.some((p: any) => p.placeId === placeId))
                const denied: any[] = policy?.deniedPlaces || []
                setOtaExempt(denied.some((p: any) => p.placeId === placeId))
            })
            .catch(() => { setOtaOptIn(null); setOtaExempt(null); setOtaMode('') })
    }

    useEffect(() => {
        setOtaOptIn(null)
        setOtaExempt(null)
        fetchOtaOptIn()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placeId])

    const toggleOtaOptIn = () => {
        if (otaOptIn === null || otaBusy || otaExempt === true) return
        setOtaBusy(true)
        Request.post(url.OTA_PLACE_ALLOW, { placeId, enabled: !otaOptIn })
            .then(() => {
                toast.success(!otaOptIn ? 'Auto-update enabled for this place' : 'Auto-update disabled for this place')
                fetchOtaOptIn()
            })
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Failed to change auto-update'))
            .finally(() => setOtaBusy(false))
    }

    const toggleOtaExempt = () => {
        if (otaExempt === null || otaBusy) return
        setOtaBusy(true)
        Request.post(url.OTA_PLACE_DENY, { placeId, enabled: !otaExempt })
            .then(() => {
                toast.success(!otaExempt ? 'Place exempted from auto-update' : 'Exemption removed for this place')
                fetchOtaOptIn()
            })
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Failed to change exemption'))
            .finally(() => setOtaBusy(false))
    }

    // Auto-poll every 30s so connectivity (WiFi/Mesh icons, online counts)
    // reflects the backend's latest known state without a manual reload.
    // Silent — never blanks the page.
    useEffect(() => {
        const id = setInterval(() => fetchData(true), 30000)
        return () => clearInterval(id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, placeId])

    const handlePlaceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const place = placeList.find((p: any) => p.id === e.target.value)
        if (place) {
            navigate(`/users/profile/${userId}/places/${place.id}/overview`)
        }
    }

    const renderTab = () => {
        const placeData = { rooms, pcbs, relays, switches, devices, owners, members, placeId: placeId!, userId: userId! }
        switch (activeTab) {
            case 'overview': return <Overview {...placeData} />
            case 'modules': return <PlaceModules {...placeData} />
            case 'appliances': return <PlaceAppliances {...placeData} />
            case 'manual-controls': return <PlaceManualControls {...placeData} />
            case 'users': return <PlaceUsers {...placeData} />
            case 'logs': return <PlaceLogs {...placeData} />
            case 'health': return <PlaceHealth {...placeData} />
            case 'network-management': return <NetworkManagement {...placeData} />
            default: return <Overview {...placeData} />
        }
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <div style={{ marginBottom: 0 }}>
                    <Breadcrumb
                        title="Users"
                        breadcrumbItem={userData ? `${userData.firstname} ${userData.lastname}` : 'Profile'}
                        titleClick="/users"
                    />
                </div>
                <ToastContainer />

                {/* Compact user + place strip */}
                {userData && (() => {
                    const fullName = `${userData.firstname || ''} ${userData.lastname || ''}`.trim()
                    const email = userData.email || ''
                    const mobile = userData.mobile || ''
                    const primary = fullName || email || mobile || '(no contact)'
                    const secondaries: string[] = []
                    if (fullName) {
                        if (email) secondaries.push(email)
                        if (mobile) secondaries.push(mobile)
                    } else if (email && mobile) {
                        // primary already shows email; surface mobile separately
                        secondaries.push(mobile)
                    }
                    const lastLogin = userData.lastLogin ? convertLocalDate(userData.lastLogin) : ''
                    const lastAction = userData.lastAction ? convertLocalDate(userData.lastAction) : ''
                    const created = userData.createdAt ? convertLocalDate(userData.createdAt) : ''
                    return (
                        <div
                            style={{
                                background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8,
                                padding: '8px 14px', marginTop: -12, marginBottom: 8,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                                <div style={{ flex: 1, minWidth: 240 }}>
                                    {/* Single wrapping identity line — name, contacts, ids AND the
                                        created/last-seen chips together (vertical budget: the
                                        module table must fit without page scroll, Ram 2026-08-05) */}
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 15, fontWeight: 600, color: '#101828' }}>{primary}</span>
                                        {secondaries.map((s, i) => (
                                            <span key={i} style={{ fontSize: 13, color: '#6B7280' }}>{s}</span>
                                        ))}
                                        {/* User ID belongs with the user's identity, next to email/mobile */}
                                        <IdRow label="User ID" value={userData.id} />
                                        {created && <Chip label="Created" value={created} />}
                                        {lastLogin && <Chip label="Last Login" value={lastLogin} />}
                                        {lastAction && lastAction !== lastLogin && (
                                            <Chip label="Last Action" value={lastAction} />
                                        )}
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
                                    <select
                                        className="form-control"
                                        title="Select place"
                                        value={placeId}
                                        onChange={handlePlaceChange}
                                        style={{
                                            appearance: 'auto', WebkitAppearance: 'menulist', MozAppearance: 'menulist',
                                            padding: '3px 8px', height: 28, fontSize: 13,
                                        }}
                                    >
                                        {placeList.map((p: any) => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                    {/* Place ID + auto-update chips share one wrapping line */}
                                    <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    {selectedPlace && (
                                        <IdRow label="Place ID" value={selectedPlace.id} />
                                    )}
                                        <span style={{ fontSize: 12, color: '#858D9D' }}>Auto-update</span>
                                        {otaMode === 'on' ? (
                                            <span style={{ fontSize: 12, color: '#12B76A', fontWeight: 500 }}>
                                                On (global)
                                            </span>
                                        ) : otaOptIn === null ? (
                                            <span style={{ fontSize: 12, color: '#9CA3AF' }}>…</span>
                                        ) : (
                                            <button
                                                data-testid="ota-optin-toggle"
                                                onClick={toggleOtaOptIn}
                                                disabled={otaBusy || otaExempt === true}
                                                title={otaExempt === true
                                                    ? 'Exempt from auto-update — remove the exemption first'
                                                    : otaOptIn
                                                        ? 'This place receives automatic firmware updates on every trigger. Click to disable.'
                                                        : 'This place only updates at provisioning time. Click to enable full auto-update.'}
                                                style={{
                                                    padding: '2px 10px', fontSize: 12, fontWeight: 500,
                                                    borderRadius: 10, cursor: (otaBusy || otaExempt === true) ? 'not-allowed' : 'pointer',
                                                    background: otaOptIn ? '#ECFDF3' : '#F3F4F6',
                                                    color: otaOptIn ? '#027A48' : '#475467',
                                                    border: otaOptIn ? '1px solid #ABEFC6' : '1px solid #D1D5DB',
                                                    opacity: (otaBusy || otaExempt === true) ? 0.6 : 1,
                                                }}
                                            >
                                                {otaOptIn ? '● Enabled' : '○ Off — provisioning only'}
                                            </button>
                                        )}
                                        {/* Exemption chip — rendered in EVERY mode: deny beats even
                                            global 'on', so it stays meaningful when the toggle above
                                            is replaced by the informational label. */}
                                        {otaExempt !== null && (
                                            <button
                                                data-testid="ota-exempt-toggle"
                                                onClick={toggleOtaExempt}
                                                disabled={otaBusy}
                                                title={otaExempt
                                                    ? 'This place receives NO automatic updates in any mode (manual/admin OTA still works). Click to remove the exemption.'
                                                    : 'Click to exempt this place from ALL automatic updates in any mode. Manual/admin OTA still works.'}
                                                style={{
                                                    padding: '2px 10px', fontSize: 12, fontWeight: 500,
                                                    borderRadius: 10, cursor: otaBusy ? 'not-allowed' : 'pointer',
                                                    background: otaExempt ? '#FEF3F2' : '#F3F4F6',
                                                    color: otaExempt ? '#B42318' : '#475467',
                                                    border: otaExempt ? '1px solid #FDA29B' : '1px solid #D1D5DB',
                                                    opacity: otaBusy ? 0.6 : 1,
                                                }}
                                            >
                                                {otaExempt ? '⛔ Exempt' : '○ Not exempt'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Network + Master rows — visible across every tab.
                                Master = the WIFI_BLE_DUAL_PROXY PCB (role 2 or 9),
                                per backend convention in cron.controller.js. */}
                            {(() => {
                                const placePcbs = (pcbs || []).filter((p: any) => p.placeId === placeId)
                                const placeDevices = (devices || []).filter((d: any) => d.placeId === placeId)
                                const placeSwitches = (switches || []).filter((s: any) => s.placeId === placeId)
                                const placeRooms = (rooms || []).filter((r: any) => r.placeId === placeId)
                                const placeRelays = (relays || []).filter((r: any) => r.placeId === placeId)
                                const roomMap: Record<string, string> = Object.fromEntries(placeRooms.map((r: any) => [r.id, r.name]))
                                const relayMap: Record<string, string> = Object.fromEntries(placeRelays.map((r: any) => [r.id, r.name]))
                                const masterPcb = placePcbs.find((p: any) => p.role === 2 || p.role === 9)
                                const masterMac = masterPcb?.macAddress || ''
                                const masterRoom = masterPcb ? (roomMap[masterPcb.roomId] || '') : ''
                                const masterPanel = masterPcb ? (relayMap[masterPcb.relayId] || '') : ''
                                const masterUnicast = masterPcb?.unicastAddress != null && masterPcb.unicastAddress !== -1
                                    ? String(masterPcb.unicastAddress)
                                    : ''
                                // Total modules = every PCB. Smart modules exclude plug-type
                                // sockets (98/99) — those are passive receptacles, not mesh nodes.
                                const PLUG = new Set(['98', '99'])
                                const smartPcbs = placePcbs.filter((p: any) => !PLUG.has(String(p.type)))
                                const totalModules = placePcbs.length
                                const totalSmart = smartPcbs.length
                                // pcb.isOnline is stale for mesh nodes; infer module online from
                                // its elements (any online switch on that pcbId). Master uses its
                                // own LWT-fresh isOnline.
                                // Only onlineStatus === 1 counts as online: -1 means "never
                                // seen", which must NOT read as online. Prefer the raw
                                // onlineStatus when the backend sends it; fall back to the
                                // legacy boolean for older responses that lack the raw field.
                                const isLive = (rec: any, legacyBool: any) =>
                                    rec?.onlineStatus != null ? rec.onlineStatus === 1 : legacyBool === true
                                const onlinePcbIds = new Set(
                                    placeSwitches.filter((s: any) => isLive(s, s.switchIsOnline)).map((s: any) => s.pcbId),
                                )
                                // A module's OWN liveness counts regardless of role.
                                // Gating this on role 2/9 meant that in a place where
                                // role was never assigned (-1 — which is every real
                                // place, since the App does not call
                                // place/pcb-role-assign) a module actively reporting
                                // onlineStatus === 1 was still counted offline: a live
                                // panel read "Smart Modules 0/2 online" (bench cycle-4,
                                // 2026-07-30). Control-point evidence remains the
                                // fallback for modules that do not report themselves.
                                const moduleIsOnline = (p: any) =>
                                    isLive(p, p.isOnline) || onlinePcbIds.has(p.id)
                                const onlineSmart = smartPcbs.filter(moduleIsOnline).length
                                // Mesh topic prefix: '0192' + MAC (colons stripped) per Dayro MQTT spec.
                                const macForTopic = masterMac
                                    ? (String(masterMac).startsWith('0192')
                                        ? masterMac
                                        : '0192' + String(masterMac).replace(/:/g, ''))
                                    : ''
                                const rxTopic = macForTopic ? `${macForTopic}/Rx` : ''
                                const txTopic = macForTopic ? `${macForTopic}/Tx` : ''
                                // Appliance online = its controlling switch is online.
                                // Link: switch.deviceId === device.id.
                                const switchByDevice: Record<string, any> = {}
                                for (const s of placeSwitches) {
                                    if (s.deviceId) switchByDevice[s.deviceId] = s
                                }
                                const onlineAppliances = placeDevices.filter(
                                    (d: any) => switchByDevice[d.id]?.switchIsOnline === true
                                ).length
                                const totalAppliances = placeDevices.length

                                let netLabel: string
                                let netColor = '#858D9D'
                                if (!masterPcb) {
                                    netLabel = '— no master'
                                } else if (masterPcb.isOnline === true) {
                                    netLabel = '● Online'
                                    netColor = '#16A34A'
                                } else {
                                    netLabel = '○ Offline'
                                    netColor = '#B91C1C'
                                }

                                const rowStyle: React.CSSProperties = {
                                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14,
                                    marginTop: 5, paddingTop: 5, borderTop: '1px solid #F3F4F6',
                                    fontSize: 12,
                                }
                                const Stat = ({ label, value, valueColor }: { label: string; value: React.ReactNode; valueColor?: string }) => (
                                    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
                                        <span style={{ color: '#858D9D' }}>{label}</span>
                                        <strong style={{ color: valueColor || '#344054' }}>{value}</strong>
                                    </span>
                                )
                                return (
                                    // Single wrapping row — network health + master details
                                    // together (was two stacked rows; vertical budget).
                                    <div style={rowStyle}>
                                        <Stat label="Network" value={netLabel} valueColor={netColor} />
                                        {totalModules > 0 && <Stat label="Modules" value={totalModules} />}
                                        {totalSmart > 0 && (
                                            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
                                                <span style={{ color: '#858D9D' }}>Smart</span>
                                                <strong style={{ color: '#344054' }}>{onlineSmart} / {totalSmart}</strong>
                                            </span>
                                        )}
                                        {totalAppliances > 0 && (
                                            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
                                                <span style={{ color: '#858D9D' }}>Appliances</span>
                                                <strong style={{ color: '#344054' }}>{onlineAppliances} / {totalAppliances}</strong>
                                            </span>
                                        )}
                                        {masterPcb && (
                                            <>
                                                <span style={{ color: '#D0D5DD' }}>|</span>
                                                <span style={{ color: '#858D9D', fontWeight: 600 }}>Master</span>
                                                {masterMac && <IdRow label="MAC" value={masterMac} />}
                                                {masterRoom && <Stat label="Room" value={masterRoom} />}
                                                {masterPanel && <Stat label="Panel" value={masterPanel} />}
                                                {masterUnicast && <IdRow label="Unicast" value={masterUnicast} />}
                                                {rxTopic && <IdRow label="/Rx" value={rxTopic} />}
                                                {txTopic && <IdRow label="/Tx" value={txTopic} />}
                                            </>
                                        )}
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                                            {lastUpdated && (
                                                <span style={{ color: '#9CA3AF', fontSize: 11 }}>
                                                    updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                </span>
                                            )}
                                            <button
                                                onClick={() => fetchData(true)}
                                                disabled={refreshing}
                                                title="Re-fetch latest status from backend"
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                                    padding: '2px 8px', fontSize: 12, fontWeight: 500,
                                                    background: '#fff', color: '#374151',
                                                    border: '1px solid #D1D5DB', borderRadius: 4,
                                                    cursor: refreshing ? 'not-allowed' : 'pointer',
                                                    opacity: refreshing ? 0.6 : 1,
                                                }}
                                            >
                                                <i className="bx bx-refresh" style={{ fontSize: 14 }} />
                                                {refreshing ? 'Refreshing…' : 'Refresh'}
                                            </button>
                                        </span>
                                    </div>
                                )
                            })()}
                        </div>
                    )
                })()}

                {/* Tab bar */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 10, borderBottom: '1px solid #E4E7EC' }}>
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => navigate(`${basePath}/${tab.key}`)}
                            style={{
                                padding: '6px 14px',
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                fontSize: 15,
                                fontWeight: activeTab === tab.key ? 600 : 400,
                                color: activeTab === tab.key ? '#000' : '#858D9D',
                                borderBottom: activeTab === tab.key ? '2px solid #000' : '2px solid transparent',
                                marginBottom: -1,
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Active tab content */}
                {renderTab()}
            </div>
        </React.Fragment>
    )
}

export default PlacePage
